/**
 * GoogleFitConnect — "Connect Google Fit" CTA + status pill.
 *
 * Drops into ExtraMilesPage / Settings. Stays gracefully invisible when the
 * backend reports configured=false (Google Cloud OAuth not yet provisioned).
 * Once configured + connected, renders a quiet "✓ Synced" pill and exposes a
 * one-tap "Sync now" button.
 *
 * Yuri spec 2026-05 Iter. 320 — HealthKit/Google Fit MVP.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Activity, Loader2, Plug, RefreshCw, Unplug } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../context/AppContext';

const GoogleFitConnect = ({ language = 'fr', onSync = () => {} }) => {
  const fr = String(language).startsWith('fr');
  const [status, setStatus] = useState(null); // { configured, connected, synced_at }
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/google-fit/status');
      setStatus(res.data || {});
    } catch {
      setStatus({ configured: false, connected: false });
    }
  }, []);

  useEffect(() => {
    refresh();
    // If user just came back from the OAuth callback, refresh + toast.
    const params = new URLSearchParams(window.location.search);
    if (params.get('gfit') === 'connected') {
      toast.success(fr ? 'Google Fit connecté ✅' : 'Google Fit connected ✅');
      refresh();
      // Trigger an immediate first sync.
      handleSync(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const res = await api.get('/google-fit/connect');
      const url = res?.data?.auth_url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error(fr ? 'Impossible de démarrer la connexion' : 'Could not start connection');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Google Fit · ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    try {
      await api.post('/google-fit/disconnect');
      toast.success(fr ? 'Google Fit déconnecté' : 'Google Fit disconnected');
      await refresh();
    } catch {
      toast.error(fr ? 'Erreur de déconnexion' : 'Disconnect error');
    } finally {
      setBusy(false);
    }
  };

  const handleSync = async (silent = false) => {
    setSyncing(true);
    try {
      const res = await api.post('/google-fit/sync-now');
      const steps = res?.data?.steps;
      if (typeof steps === 'number') {
        if (!silent) {
          toast.success(
            fr ? `✓ ${steps.toLocaleString('fr-CA')} pas synchronisés` : `✓ ${steps} steps synced`
          );
        }
        onSync(res.data);
      } else if (!silent) {
        toast.info(fr ? 'Aucune donnée Google Fit' : 'No Google Fit data');
      }
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || (fr ? 'Erreur de synchro' : 'Sync error'));
    } finally {
      setSyncing(false);
    }
  };

  // Hide entirely while backend is dormant — keep the UI lean.
  if (!status || status.configured === false) return null;

  return (
    <div
      data-testid="google-fit-card"
      className="rounded-xl border border-emerald-500/30 bg-black/40 backdrop-blur-sm p-4"
      style={{ boxShadow: '0 0 24px rgba(52,211,153,0.18)' }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center bg-emerald-500/15"
            style={{ boxShadow: '0 0 12px rgba(52,211,153,0.4)' }}
          >
            <Activity className="w-5 h-5 text-emerald-300" />
          </div>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-emerald-300 font-bold">
              Google Fit
            </p>
            <p className="text-[11px] text-muted-foreground truncate">
              {status.connected
                ? fr
                  ? 'Synchronisation auto · pas du jour'
                  : 'Auto-sync · today\'s steps'
                : fr
                ? 'Synchronise tes pas automatiquement'
                : 'Sync your steps automatically'}
            </p>
          </div>
        </div>

        {status.connected ? (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => handleSync(false)}
              disabled={syncing || busy}
              data-testid="google-fit-sync-btn"
              className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {fr ? 'Sync' : 'Sync'}
            </button>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={busy}
              data-testid="google-fit-disconnect-btn"
              title={fr ? 'Déconnecter' : 'Disconnect'}
              className="p-1.5 rounded-md text-muted-foreground hover:text-rose-300 hover:bg-rose-500/10 transition-colors disabled:opacity-50"
            >
              <Unplug className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleConnect}
            disabled={busy}
            data-testid="google-fit-connect-btn"
            className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider bg-emerald-500 text-black hover:bg-emerald-400 transition-colors flex items-center gap-1.5 shrink-0 disabled:opacity-50"
            style={{ boxShadow: '0 0 18px rgba(52,211,153,0.45)' }}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />}
            {fr ? 'Connecter' : 'Connect'}
          </button>
        )}
      </div>
    </div>
  );
};

export default GoogleFitConnect;
